import { useMemo, type ReactNode } from 'react';
import { SheetModal } from '@/components/SheetModal';
import clientIntakeFormSchema from '@/util/clientIntakeFormSchema.json';

interface SchemaEntry {
  type: string;
  label: string;
  group: string;
}

const SCHEMA = clientIntakeFormSchema as Record<string, SchemaEntry>;

interface IntakePayloadSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intakePayload: unknown;
  title?: string;
  subTitle?: string;
}

function formatValue(value: unknown, type: string): ReactNode {
  if (value == null || value === '') {
    return type === 'list' ? 'None' : 'N/A';
  }

  switch (type) {
    case 'string':
    case 'integer':
      return String(value);
    case 'number': {
      const n = parseFloat(String(value));
      return Number.isNaN(n) ? String(value) : `$${n.toLocaleString()}`;
    }
    case 'percent': {
      const n = parseFloat(String(value));
      return Number.isNaN(n) ? String(value) : `${n}%`;
    }
    case 'list': {
      const arr = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value.split('|||').map(s => s.trim())
          : [value];
      if (arr.length === 0) return 'None';
      return (
        <ul className="list-inside list-disc">
          {arr.map((item, i) => (
            <li key={i}>{String(item)}</li>
          ))}
        </ul>
      );
    }
    case 'date': {
      const date = new Date(String(value));
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    default:
      return String(value);
  }
}

function valueOfAnswer(answer: Record<string, any>): unknown {
  switch (answer.type) {
    case 'text':
    case 'email':
    case 'number':
    case 'date':
    case 'boolean':
      return answer[answer.type] ?? null;
    case 'choice': {
      const c = (answer.choice ?? {}) as Record<string, any>;
      if (c.other !== undefined && c.other !== null) return c.other;
      return c.label ?? null;
    }
    case 'choices': {
      const cs = (answer.choices ?? {}) as Record<string, any>;
      const labels = Array.isArray(cs.labels) ? cs.labels : [];
      if (cs.other && labels.length) return [...labels, cs.other];
      if (cs.other) return cs.other;
      return labels.length ? labels : null;
    }
    case 'phone_number':
      return answer.phone_number ?? answer.text ?? null;
    default:
      return null;
  }
}

function extractFromTypeformWebhook(
  payload: Record<string, any>,
): Record<string, unknown> | null {
  const fr = payload.form_response;
  if (!fr?.definition?.fields || !Array.isArray(fr.answers)) return null;

  const idToRef = new Map<string, string>();
  for (const field of fr.definition.fields as Array<Record<string, any>>) {
    if (field?.id && field?.ref) idToRef.set(field.id, field.ref);
  }

  const out: Record<string, unknown> = {};
  for (const answer of fr.answers as Array<Record<string, any>>) {
    const ref = idToRef.get(answer?.field?.id) ?? answer?.field?.ref;
    if (!ref) continue;
    const value = valueOfAnswer(answer);
    if (value !== null && value !== undefined) out[ref] = value;
  }

  if (fr.hidden && typeof fr.hidden === 'object') {
    Object.entries(fr.hidden as Record<string, unknown>).forEach(([k, v]) => {
      if (!(k in out)) out[k] = v;
    });
  }

  return out;
}

function parsePayload(payload: unknown): Record<string, unknown> | null {
  if (!payload) return null;

  let obj: Record<string, any> | null = null;
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        obj = parsed;
      }
    } catch {
      return null;
    }
  } else if (typeof payload === 'object' && !Array.isArray(payload)) {
    obj = payload as Record<string, any>;
  }

  if (!obj) return null;

  if (obj.form_response?.definition?.fields && Array.isArray(obj.form_response?.answers)) {
    return extractFromTypeformWebhook(obj);
  }

  return obj;
}

interface Row {
  fieldName: string;
  label: string;
  group: string;
  type: string;
  value: unknown;
}

function isBlank(value: unknown): boolean {
  return (
    value == null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function IntakePayloadSheet({
  open,
  onOpenChange,
  intakePayload,
  title = 'Intake payload',
  subTitle = 'Snapshot of the intake form this test case was captured from.',
}: IntakePayloadSheetProps) {
  const grouped = useMemo<[string, Row[]][]>(() => {
    const parsed = parsePayload(intakePayload);
    if (!parsed) return [];

    const byField: Record<string, unknown> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      const fieldName = key.split('::')[0];
      if (!(fieldName in SCHEMA)) return;
      const existing = byField[fieldName];
      if (existing === undefined || (isBlank(existing) && !isBlank(value))) {
        byField[fieldName] = value;
      }
    });

    const rows: Row[] = Object.entries(byField).map(([fieldName, value]) => ({
      fieldName,
      value,
      label: SCHEMA[fieldName].label,
      group: SCHEMA[fieldName].group,
      type: SCHEMA[fieldName].type,
    }));

    const bySection: Record<string, Row[]> = {};
    rows
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach(row => {
        bySection[row.group] = bySection[row.group] ?? [];
        bySection[row.group].push(row);
      });

    return Object.entries(bySection).sort((a, b) => a[0].localeCompare(b[0]));
  }, [intakePayload]);

  return (
    <SheetModal
      open={open}
      onClose={() => onOpenChange(false)}
      title={title}
      subTitle={subTitle}
      className="sm:min-w-[85%] md:min-w-[600px] lg:min-w-[800px] xl:min-w-[900px]"
    >
      <div
        data-hj-suppress
        data-private
        className="pb-8 pt-3 text-gray-800"
      >
        {grouped.length === 0 ? (
          <p className="text-sm text-gray-600">
            No recognizable intake fields in this snapshot.
          </p>
        ) : (
          grouped.map(([group, items]) => (
            <section
              key={group}
              className="mt-8 first:mt-0"
            >
              <h2 className="text-base font-semibold capitalize text-gray-800">
                {group}
              </h2>
              <dl className="mt-2 divide-y divide-gray-100 border-t border-gray-200">
                {items.map(item => (
                  <div
                    key={item.fieldName}
                    className="grid grid-cols-1 gap-2 py-3 sm:grid-cols-3 sm:gap-4"
                  >
                    <dt className="text-sm font-medium leading-6 text-gray-800">
                      {item.label}
                    </dt>
                    <dd className="text-sm leading-6 text-gray-700 sm:col-span-2">
                      {formatValue(item.value, item.type)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))
        )}
      </div>
    </SheetModal>
  );
}
